import { useAppDispatch } from 'app/hooks';
import ExistingComponentSpecFields from 'components/common/existing-component-spec-fields';
import FormInput from 'components/common/form-input';
import Button from 'components/inputs/button';
import type { ExistingSystemComponentType } from 'data/types';
import { useUpdateExistingEquipmentComponentMutation } from 'features/api/project-api';
import { updateExistingSystemComponent } from 'features/structure';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { notification } from 'utils/ui';

// In-card draft editor for one existing-equipment component in the Compatible
// Combinations Browser (AHA-6681). Live typing touches only local draft state
// — no Redux dispatch (which would arm the autosave watcher), no network. On
// Save, strict order: (1) surgical PATCH so the persisted specs are current
// before any refetch anchors them, (2) Redux sync so the project page
// reflects the edit, (3) onSaved so the modal bumps its refetch nonce.
export default function ExistingEquipmentComponentEditor({
  component,
  onSaved,
  onCancel
}: {
  component: ExistingSystemComponentType;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const dispatch = useAppDispatch();
  const [updateExistingEquipmentComponent, { isLoading: saving }] =
    useUpdateExistingEquipmentComponentMutation();
  // Only the keys the user touched; a cleared field is an explicit
  // `undefined` entry so the spread over the component (and the merge
  // reducer's spread) overwrites the old value instead of keeping it.
  const [draft, setDraft] = useState<Record<string, unknown>>({});

  const draftValue = (propKey: string) =>
    propKey in draft ? draft[propKey] : (component as any)[propKey];

  const setDraftValue = (propKey: string, value: unknown) => {
    setDraft((prev) => ({ ...prev, [propKey]: value }));
  };

  const handleSave = async () => {
    const merged = { ...component, ...draft } as ExistingSystemComponentType;
    try {
      await updateExistingEquipmentComponent({ component: merged }).unwrap();
    } catch {
      notification('Failed to save equipment changes', 'error');
      return;
    }
    // The dispatch re-arms the debounced autosave with post-edit state. An
    // already-in-flight autosave carrying pre-edit specs could land after the
    // PATCH, but the re-armed save re-persists the correct values within ~1s
    // (self-healing) — same accepted race as the Replacing toggle dual-write.
    //
    // flushSync forces ONE render pass for the Redux sync and the modal's
    // nonce bump: the useSyncExternalStore update from dispatch is scheduled
    // on the sync lane while onSaved's setState lands on the default lane, so
    // automatic batching alone would split them into two renders — and two
    // combinations refetches (the modal's fetch effect keys on both the
    // components-derived kept set and the nonce).
    flushSync(() => {
      dispatch(updateExistingSystemComponent(merged));
      onSaved();
    });
  };

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
        <FormInput
          type='text'
          label='Model Number'
          value={(draftValue('modelNumber') as string) ?? ''}
          onChange={(value) => setDraftValue('modelNumber', value)}
          placeholder='Enter Model #'
          disabled={saving}
        />
        <FormInput
          type='text'
          label='Manufacturer'
          value={(draftValue('manufacturer') as string) ?? ''}
          onChange={(value) => setDraftValue('manufacturer', value)}
          placeholder='Enter Manufacturer'
          disabled={saving}
        />
      </div>
      <ExistingComponentSpecFields
        componentType={component.type}
        values={{ ...component, ...draft } as Record<string, any>}
        onChange={setDraftValue}
        disabled={saving}
      />
      <div className='flex items-center gap-2'>
        <Button text='Save' onClick={handleSave} disabled={saving} />
        <Button
          text='Cancel'
          color='white'
          onClick={onCancel}
          disabled={saving}
        />
      </div>
    </div>
  );
}
